import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext, useEffect } from 'react';
import { Consumer } from '~/constants/common/consumer';
import { useIsNative } from '~/context/common/DeviceContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/home/ModalContext';
import buildComposerRedirectUrl from '~/utilities/composer/buildComposerRedirectUrl';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const eventCorrelationId = useEventCorrelationId();
  const isComposerReady = useIsComposerReady();
  const isNative = useIsNative();
  const { zip } = useUserLocation();

  // Listeners
  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ cvnaid, filters, source }) => {
        if (source !== 'composer' || isNative) return;

        if (cvnaid) {
          window.location.href = buildComposerRedirectUrl(Consumer.HOME, {
            cvnaid
          });

          return;
        }

        const validatedFilters = parseComposerFilters(filters);
        if (!validatedFilters) return;

        window.location.href = buildComposerRedirectUrl(Consumer.HOME, {
          filters: validatedFilters
        });
      },
      [isNative]
    )
  );
  // Non-SPA nav (Home is Pages Router, VDP is App Router), so this unmounts
  // everything on navigation — no need to explicitly close composer first,
  // unlike the SPA case on SRP.
  useCoreEvents(
    eventNames.composer.mfe.vdp,
    useCallback(
      ({ source, vehicleId }) => {
        if (source !== 'composer' || isNative || !vehicleId) return;

        window.location.href = `/vehicle/${vehicleId}`;
      },
      [isNative]
    )
  );

  // Dispatchers
  useEffect(() => {
    if (!isComposerReady) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: ['update_search', 'vdp'],
        merchContextId: eventCorrelationId,
        url: window.location.href,
        zip5: zip
      },
      source: 'page',
      surface: 'home'
    });
  }, [eventCorrelationId, isComposerReady, zip]);
}
